import { useCallback } from 'react';

import { useNavigate } from '@change-corgi/core/react/router';
import { useTracking } from '@change-corgi/core/react/tracking';

import { usePetitionDetailsPageData, usePetitionDetailsPageUserData } from 'src/app/pages/petitionDetails/pageContext';
import { useCurrentUserAsync } from 'src/app/shared/hooks/session';
import { isLoaded } from 'src/app/shared/utils/async';

type Result = ModelHookResult<
	{
		username: string;
		userHasSigned: boolean;
	},
	{
		createVideoClickHandler: () => void;
	}
>;

export function useCreateVideo(): Result {
	const navigate = useNavigate();
	const track = useTracking();
	const {
		data: {
			petition: { id: petitionId },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { signed },
	} = usePetitionDetailsPageUserData();
	const currentUserBase = useCurrentUserAsync();
	const currentUser = isLoaded(currentUserBase) && currentUserBase.value?.shortDisplayName;

	const createVideoClickHandler = useCallback(() => {
		void track('supporter-voices-create-a-video-button-click', {
			petition_id: petitionId,
		});

		/**
		 * Although slower, this triggers a full page reload on the /p/:slug/actions page
		 * (ie. beyond react router's client side routing) in order to get the utm data reflecting on amplitude
		 */
		navigate(`/p/${petitionId}/actions?utm_campaign=petition_details`, { forceMode: 'external' });
	}, [navigate, petitionId, track]);

	return {
		data: {
			username: currentUser || '',
			userHasSigned: signed,
		},
		actions: {
			createVideoClickHandler,
		},
	};
}
